/**
 * 単一系列の時系列チャート (棒 or 折れ線)。
 *
 * 体重と体脂肪率のようにスケールの違う指標は 2 軸にせず、
 * このチャートを縦に並べて別々に描く。
 */
import { useEffect, useRef, useState } from 'react';
import type { Bucket } from '../charts';

type Point = { bucket: Bucket; value: number | null };

type Props = {
  title: string;
  unit: string;
  color: string;
  kind: 'bar' | 'line';
  points: Point[];
  /** ツールチップと直接ラベルの表示形式 */
  format: (v: number) => string;
  /** 見出しの右に出す現在値・変化量 */
  headline?: { value: string; sub?: string };
  height?: number;
  /** 縦軸の目盛り用。軸の最大値も渡されるので、桁を揃えた表記にできる。 */
  formatTick?: (v: number, axisMax: number) => string;
  /**
   * 折れ線をつなぐ許容欠測数。これを超えて空いたら線を切る。
   * 毎日測らない体重で「破線」に見えるのを防ぎつつ、長期の空白は繋がない。
   */
  maxGap?: number;
  /** 幅を測らず固定する (目視確認用に SVG を書き出すときに使う) */
  fixedWidth?: number;
};

/** 描画に実寸が必要なので、コンテナ幅を測る */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
}

/** 目盛りが読みやすい丸い刻み幅にする */
function niceStep(span: number, targetLines: number): number {
  const raw = span / targetLines;
  const mag = 10 ** Math.floor(Math.log10(raw || 1));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (raw <= mag * m) return mag * m;
  }
  return mag * 10;
}

const PAD = { top: 16, right: 12, bottom: 20, left: 40 };

export function TimeChart({
  title,
  unit,
  color,
  kind,
  points,
  format,
  headline,
  height = 156,
  formatTick,
  maxGap = 3,
  fixedWidth,
}: Props) {
  const [wrapRef, measured] = useWidth<HTMLDivElement>();
  const width = fixedWidth ?? measured;
  const [hover, setHover] = useState<number | null>(null);

  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const hasData = values.length > 0;

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;

  // 棒は 0 起点、折れ線は値の範囲に寄せる (体重の 1kg の差を見たいため)
  const rawMin = hasData ? Math.min(...values) : 0;
  const rawMax = hasData ? Math.max(...values) : 1;
  let min = kind === 'bar' ? 0 : rawMin;
  let max = rawMax;
  if (kind === 'line') {
    const pad = (rawMax - rawMin) * 0.25 || Math.max(rawMax * 0.02, 0.5);
    min = rawMin - pad;
    max = rawMax + pad;
  }
  if (max === min) max = min + 1;

  const step = niceStep(max - min, 3);
  const gridMin = Math.floor(min / step) * step;
  const gridMax = Math.ceil(max / step) * step;
  const lo = kind === 'bar' ? 0 : gridMin;
  const hi = gridMax;

  const y = (v: number) => PAD.top + plotH - ((v - lo) / (hi - lo)) * plotH;
  const bandW = points.length > 0 ? plotW / points.length : 0;
  const cx = (i: number) => PAD.left + bandW * (i + 0.5);

  const gridLines: number[] = [];
  for (let v = lo; v <= hi + 1e-9; v += step) gridLines.push(Math.round(v * 1000) / 1000);

  // 軸ラベルは間引く。右端 (最新) を基準に等間隔で置くので、末尾で重ならない。
  const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(3, Math.floor(width / 62))));
  const labelIndexes = new Set<number>();
  for (let i = points.length - 1; i >= 0; i -= labelEvery) labelIndexes.add(i);

  const lastIndex = (() => {
    for (let i = points.length - 1; i >= 0; i--) if (points[i]!.value !== null) return i;
    return -1;
  })();

  /**
   * 折れ線は maxGap までの欠測なら繋ぎ、それを超えたら切る。
   * 実測点には必ず丸を置くので、どこが計測日かは線と別に読める。
   */
  const segments: { i: number; v: number }[][] = [];
  if (kind === 'line') {
    let current: { i: number; v: number }[] = [];
    let lastIdx = -Infinity;
    points.forEach((p, i) => {
      if (p.value === null) return;
      if (i - lastIdx > maxGap + 1 && current.length > 0) {
        segments.push(current);
        current = [];
      }
      current.push({ i, v: p.value });
      lastIdx = i;
    });
    if (current.length > 0) segments.push(current);
  }

  const markerR = points.length > 20 ? 3 : 4;
  const active = hover !== null ? points[hover] : undefined;

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (bandW <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const i = Math.floor((e.clientX - rect.left - PAD.left) / bandW);
    setHover(i >= 0 && i < points.length ? i : null);
  };

  return (
    <section className="chart">
      <header className="chart__head">
        <h3 className="chart__title">
          {title}
          <span className="chart__unit">{unit}</span>
        </h3>
        {headline && (
          <div className="chart__headline">
            <span className="chart__value" style={{ color }}>
              {headline.value}
            </span>
            {headline.sub && <span className="chart__sub">{headline.sub}</span>}
          </div>
        )}
      </header>

      <div className="chart__plot" ref={wrapRef}>
        {width > 0 && (
          <svg
            width={width}
            height={height}
            role="img"
            aria-label={`${title} の推移`}
            onPointerMove={onMove}
            onPointerLeave={() => setHover(null)}
            style={{ touchAction: 'pan-y' }}
          >
            {/* 目盛り。線は控えめに、数字は muted で */}
            {gridLines.map((v) => (
              <g key={v}>
                <line
                  x1={PAD.left}
                  x2={width - PAD.right}
                  y1={y(v)}
                  y2={y(v)}
                  stroke="var(--line-soft)"
                  strokeWidth="1"
                />
                <text x={PAD.left - 7} y={y(v) + 3.5} className="chart__tick" textAnchor="end">
                  {(formatTick ?? format)(v, hi)}
                </text>
              </g>
            ))}

            {/* ホバー位置の十字線 */}
            {hover !== null && (
              <line
                x1={cx(hover)}
                x2={cx(hover)}
                y1={PAD.top - 4}
                y2={PAD.top + plotH}
                stroke="var(--line)"
                strokeWidth="1"
              />
            )}

            {kind === 'bar'
              ? points.map((p, i) => {
                  if (!p.value) return null;
                  // 隣の棒との間に 2px の隙間を空ける
                  const w = Math.max(2, bandW - 2);
                  const top = y(p.value);
                  const h = PAD.top + plotH - top;
                  const r = Math.min(4, w / 2, h);
                  return (
                    <path
                      key={p.bucket.key}
                      d={`M${cx(i) - w / 2},${PAD.top + plotH} v${-(h - r)} a${r},${r} 0 0 1 ${r},${-r} h${w - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${h - r} Z`}
                      fill={color}
                      opacity={hover === null || hover === i ? 1 : 0.45}
                    />
                  );
                })
              : segments.map((seg, si) => (
                  <g key={si}>
                    <path
                      d={seg.map((pt, k) => `${k === 0 ? 'M' : 'L'}${cx(pt.i)},${y(pt.v)}`).join(' ')}
                      fill="none"
                      stroke={color}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {/* 実測点には丸を置く (重なりを避けるため下地に surface の輪郭) */}
                    {seg.map((pt) => (
                      <circle
                        key={pt.i}
                        cx={cx(pt.i)}
                        cy={y(pt.v)}
                        r={markerR}
                        fill={color}
                        stroke="var(--surface)"
                        strokeWidth="2"
                      />
                    ))}
                  </g>
                ))}

            {/* 最新値だけ強調する (すべての点に数字は置かない) */}
            {kind === 'line' && lastIndex >= 0 && (
              <circle
                cx={cx(lastIndex)}
                cy={y(points[lastIndex]!.value!)}
                r="5"
                fill={color}
                stroke="var(--surface)"
                strokeWidth="2"
              />
            )}

            {/* 横軸 */}
            {points.map((p, i) =>
              labelIndexes.has(i) ? (
                <text
                  key={p.bucket.key}
                  x={cx(i)}
                  y={height - 6}
                  className="chart__tick"
                  textAnchor="middle"
                >
                  {p.bucket.label}
                </text>
              ) : null,
            )}
          </svg>
        )}

        {active && (
          <div
            className="chart__tip"
            style={{
              left: `${Math.min(Math.max(cx(hover!), 66), Math.max(width - 66, 66))}px`,
            }}
          >
            <span className="chart__tip-when">{active.bucket.caption}</span>
            <span className="chart__tip-value" style={{ color }}>
              {active.value === null ? '記録なし' : `${format(active.value)} ${unit}`}
            </span>
          </div>
        )}

        {!hasData && <div className="chart__empty">この期間の記録がありません</div>}
      </div>
    </section>
  );
}
